import React, { useRef, useState } from "react";
import "./ManageAccount.css";

import { useLogin } from "../LoginProvider";
import { db, auth, storage, ref, uploadBytes, getDownloadURL } from "../firebase";

import {
  collection, query, where, limit, getDocs,
  doc, updateDoc, serverTimestamp
} from "firebase/firestore";
import { updateEmail, updateProfile } from "firebase/auth";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const USERS_COLLECTION = "users";

// normalise un numéro pour la comparaison
const normalizePhone = (s = "") =>
  s.toString().replace(/[^\d+]/g, "").replace(/^00/, "+"); // "90 00 00 00" -> "90000000"

export default function ManageAccount({ onSubmit }) {
  const { user, setUser } = useLogin(); // <-- pas de optional chaining ici

  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(user?.photo || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [form, setForm] = useState({
    firstName: user?.name?.split(" ")?.[0] || "",
    lastName: user?.name?.split(" ")?.slice(1).join(" ") || "",
    email: user?.email || "",
    phone: user?.phone || "",
    gender: user?.gender || "",
    govtId: user?.govtId || "",
    taxNumber: user?.taxNumber || "",
    taxCountry: user?.taxCountry || "Togo",
    address: user?.address || "",
  });

  if (!user?.id) {
    return (
      <>
        <ToastContainer />
        <div className="container-mc">
          <div className="alert error">Veuillez vous connecter pour gérer votre compte.</div>
        </div>
      </>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErr("");
    setOk("");
  };

  const handleUploadClick = () => fileRef.current?.click();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const handleDeleteAvatar = () => {
    setFile(null);
    setPreview("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;

    setErr("");
    setOk("");
    setLoading(true);

    try {
      // 0) Prépare les données propres
      const name = [form.firstName, form.lastName].filter(Boolean).join(" ").trim();
      const phone_norm = normalizePhone(form.phone);
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        name,
        email: form.email.trim(),
        phone: form.phone.trim(),
        phone_norm, // champ technique pour les recherches
        gender: form.gender || "",
        govtId: form.govtId || "",
        taxNumber: form.taxNumber || "",
        taxCountry: form.taxCountry || "",
        address: form.address || "",
        updatedAt: serverTimestamp(),
      };

      
      // 2) Upload avatar (si un fichier a été choisi)
      let photoURL = user.photo || "";
      if (file) {
        const path = `avatars/${user.id}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        photoURL = await getDownloadURL(storageRef);
        payload.photo = photoURL;
      } else {
        payload.photo = preview || "";
      }

      // 3) Mise à jour du profil Firebase Auth (displayName, photoURL, email)
      try {
        if (auth.currentUser) {
          // displayName / photoURL
          const needProfileUpdate =
            auth.currentUser.displayName !== name ||
            (photoURL && auth.currentUser.photoURL !== photoURL);

          if (needProfileUpdate) {
            await updateProfile(auth.currentUser, {
              displayName: name,
              photoURL: photoURL || auth.currentUser.photoURL || null,
            });
          }

          // email (si modifié)
          if (
            payload.email &&
            auth.currentUser.email &&
            payload.email.toLowerCase() !== auth.currentUser.email.toLowerCase()
          ) {
            try {
              await updateEmail(auth.currentUser, payload.email);
            } catch (eEmail) {
              // Si recent-login requis, on stoppe et on informe l'utilisateur
              console.warn("updateEmail error:", eEmail);
              setErr(
                "Impossible de mettre à jour l’e-mail (connexion récente requise). Déconnectez-vous puis reconnectez-vous et réessayez."
              );
              setLoading(false);
              return;
            }
          }
        }
      } catch (eProf) {
        console.warn("updateProfile error:", eProf);
        // On n'arrête pas pour un simple échec de profil, on continue Firestore
      }

      // 4) Mettre à jour le document utilisateur (Firestore)
      await updateDoc(doc(db, USERS_COLLECTION, user.id), payload);

      // 5) Mettre à jour le contexte local
      const updatedLocal = { ...user, ...payload };
      if (setUser) setUser(updatedLocal);

      setOk("Modifications enregistrées.");
      onSubmit?.(payload);
    } catch (e2) {
      console.error(e2);
      setErr("Impossible d’enregistrer les modifications.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="container-mc">
        {/* Header avatar */}
        <div className="top-mc">
          <div className="avatar-wrap-mc">
            <div className="avatar-mc">
              {preview ? (
                <img src={preview} alt="avatar" />
              ) : (
                <span className="avatar-initial-mc">
                  {(form.firstName?.[0] || user?.name?.[0] || "U").toUpperCase()}
                </span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                hidden
              />
            </div>
          </div>

          <div className="avatar-actions-mc">
            <button type="button" className="btn-primary-mc" onClick={handleUploadClick}>
              Ajouter
            </button>
            <button type="button" className="btn-ghost-mc" onClick={handleDeleteAvatar}>
              Supprimer Profil
            </button>
          </div>
        </div>

        {/* Alerts */}
        {err && <div className="alert error">{err}</div>}
        {ok && <div className="alert success">{ok}</div>}

        {/* Form */}
        <form className="form-mc" onSubmit={handleSubmit}>
          <div className="grid-2-mc">
            <label className="field-mc">
              <span>Nom <em>*</em></span>
              <input
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
              />
            </label>
            <label className="field-mc">
              <span>Prénoms <em>*</em></span>
              <input
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div className="grid-2-mc">
            <label className="field-mc">
              <span>E-mail</span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                disabled={true}
              />
            </label>
            <label className="field-mc">
              <span>Téléphone <em>*</em></span>
              <div className="phone-wrap-mc">
                <span className="flag-mc">TG</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </label>
          </div>

          <div className="grid-2-mc">
            <fieldset className="field-mc">
              <span>Genre</span>
              <div className="radio-row-mc">
                <label className={`radio-mc ${form.gender === "Male" ? "checked" : ""}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={form.gender === "Male"}
                    onChange={handleChange}
                  />
                  <span>Masculin</span>
                </label>
                <label className={`radio-mc ${form.gender === "Female" ? "checked" : ""}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={form.gender === "Female"}
                    onChange={handleChange}
                  />
                  <span>Féminin</span>
                </label>
              </div>
            </fieldset>

            {form.govtId !== "" ? (
              <label className="field-mc">
                <span>Code de Parainage</span>
                <input name="govtId" disabled value={form.govtId} onChange={handleChange} />
              </label>
            ) : null}
          </div>

          <div className="actions-mc">
            <button className="btn-primary-mc" type="submit" disabled={loading}>
              {loading ? "Enregistrement..." : "Enregistrer le changement"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
